import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {LmsCardLocationsEnum, trackLmsWithLocation} from '../../../../settings/lms/lmsHelpers';
import {
  getHasIndividualTrainingAccess,
  getHasWebinarTrainingAccess,
} from '../../../../store/data/company/companySelectors';
import {lmsFrontAcademyUrl, webinarTrainingUrl} from '../../../../store/stateless/navigationActions';
import {HelpTopicCard} from '../../../help/helpTopicCard';
import {
  individualTrainingCardContentFull,
  lmsCardContentFull,
  webinarTrainingCardContentFull,
} from '../../../help/helpTopicCardContent';
import {useIndividualTrainingChiliPiperDialog} from '../../../onboarding/chiliPiper/individualTrainingChiliPiperDialog';

/*
 * Style.
 */

const StyledResourcesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledResourceTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.greys.shade90};
`;

/*
 * Component.
 */

export const RecommendedHelpSection: FC = (props) => {
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();
  const hasIndividualTrainingAccess = useAppSelector(getHasIndividualTrainingAccess);
  const hasWebinarTrainingAccess = useAppSelector(getHasWebinarTrainingAccess);

  const [individualTrainingDialog, onIndividualTrainingClick] = useIndividualTrainingChiliPiperDialog(
    TopBarDataEventObject.BOOK_A_SETUP_CALL,
  );

  const onWebinarTrainingClick = useCallback(() => {
    navigateToLink(webinarTrainingUrl);
    trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.LIVE_WEBINAR);
  }, [navigateToLink, trackDataEvent]);

  const onFrontAcademyClick = useCallback(() => {
    navigateToLink(lmsFrontAcademyUrl);
    trackLmsWithLocation(LmsCardLocationsEnum.P0_HELP_AND_TIPS);
  }, [navigateToLink]);

  return (
    <>
      <StyledResourcesSection>
        <StyledResourceTitle>
          <Trans id="help-menu-recommended-resources-title">Recommended</Trans>
        </StyledResourceTitle>
        {hasIndividualTrainingAccess && (
          <HelpTopicCard content={individualTrainingCardContentFull} onClick={onIndividualTrainingClick} />
        )}
        {!hasIndividualTrainingAccess && hasWebinarTrainingAccess && (
          <HelpTopicCard content={webinarTrainingCardContentFull} onClick={onWebinarTrainingClick} />
        )}
        <HelpTopicCard content={lmsCardContentFull} onClick={onFrontAcademyClick} />
      </StyledResourcesSection>
      {individualTrainingDialog}
    </>
  );
};
